import { action, computed, flow, makeObservable, observable, toJS } from 'mobx';
import client from '~/services/client';
import logger from '~/services/logger';
import { LoadingStatus, paginationCast, TPagination } from '~/types/main';
import { ordersCast, TOrder } from '~/types/orders';

const defaultPagination = { currentPage: 1, lastPage: 0, totalRows: 0, perPage: 0 };

export class BaseOrdersStore {
  constructor() {
    makeObservable(this, {
      _checkOrderMeetsFilters: action,
      _getOrders: action,
      _makeFetchOrdersParams: action,
      getOrdersParams: action,
      _orders: observable,
      _updateOrder: action,
      changeSearch: action,
      checkOrderMeetsCurrentTabAndFilters: action,
      deleteOrder: action,
      getOrders: action,
      handleUpdateOrder: action,
      lastOpenedOrderId: observable,
      currentOffset: observable,
      perPage: observable,
      orders: computed,
      ordersLoadingStatus: observable,
      pagination: observable,
      resetSearch: action,
      searchValue: observable,
      clear: action,
    });
  }

  public lastOpenedOrderId: null | string = null;
  public isHaveNextPages: boolean = true;
  public currentOffset: number | null = null;
  public perPage: number = 0;
  _orders = observable.map<string, TOrder>();

  public pages: number[] = [];

  private ordersGetAbortController = new AbortController();
  private ordersGettingTimeoutId: NodeJS.Timeout | undefined = undefined;

  public ordersLoadingStatus: LoadingStatus = LoadingStatus.Init;

  public pagination: TPagination = defaultPagination;
  public searchValue: string = '';

  _getOrders = flow(function* (this: BaseOrdersStore, offset?: number): any {
    this.ordersGetAbortController.abort();
    const ordersGetAbortController = new AbortController();
    this.ordersGetAbortController = ordersGetAbortController;

    try {
      this.ordersLoadingStatus = LoadingStatus.Loading;
      const params = this._makeFetchOrdersParams(offset);

      const response = yield client.get('/v4/orders', { params, signal: this.ordersGetAbortController.signal });
      const { data = [], current_offset, per_page, ...pagination } = response;
      this.pagination = paginationCast(pagination);

      if (!current_offset) {
        this._orders = ordersCast(data);
      } else {
        this._orders = observable.map([...this._orders.entries(), ...ordersCast(data).entries()]);
      }

      this.currentOffset = current_offset;
      this.perPage = per_page;
      this.isHaveNextPages = Boolean(data?.length / per_page === 1);

      this.ordersLoadingStatus = LoadingStatus.Success;
    } catch (error: any) {
      if (ordersGetAbortController.signal.aborted) {
        this.ordersLoadingStatus = LoadingStatus.Aborted;
      } else {
        this.ordersLoadingStatus = LoadingStatus.Error;
        logger.error({ error, place: 'baseOrdersStore _getOrders' });
      }
    }
  }).bind(this);

  protected _getOrdersWithDelay = (offset?: number) => {
    clearTimeout(this.ordersGettingTimeoutId);

    this.ordersGettingTimeoutId = setTimeout(() => {
      this._getOrders(offset);
    }, 700);
  };

  protected _getOrdersWithNoDelay = (offset?: number) => {
    clearTimeout(this.ordersGettingTimeoutId);
    this._getOrders(offset);
  };

  public resetSearch = () => {
    this.searchValue = '';
    this._getOrdersWithNoDelay(0);
  };

  public changeSearch = (searchValue: string = '') => {
    if (searchValue !== this.searchValue) {
      this.searchValue = searchValue;
      this._getOrdersWithDelay(0);
    }
  };

  // eslint-disable-next-line unused-imports/no-unused-vars
  _makeFetchOrdersParams(offset?: number, per_page: number = 25) {
    return {};
  }

  // eslint-disable-next-line unused-imports/no-unused-vars
  getOrdersParams(per_page: number = 25) {
    return {
      offset: typeof this.currentOffset === 'number' ? this.currentOffset + per_page : 0,
      ...(per_page && { per_page }),
    };
  }

  // eslint-disable-next-line unused-imports/no-unused-vars
  _checkOrderMeetsFilters(order: TOrder): boolean {
    return true;
  }

  // eslint-disable-next-line unused-imports/no-unused-vars
  checkOrderMeetsCurrentTabAndFilters(order: TOrder): boolean {
    return true;
  }

  public getOrders = (offset?: number) => {
    if (this.ordersLoadingStatus === LoadingStatus.Init) {
      this._getOrdersWithNoDelay(0);
    } else {
      this._getOrdersWithNoDelay(offset);
    }
  };

  _updateOrder = (order: TOrder, orderMeetsFilters: boolean = true) => {
    if (orderMeetsFilters) {
      if (this._orders.has(order.id)) {
        if (!order.viewed) {
          // lift order up, if not viewed
          const entries = [...this._orders.entries()].filter((entry) => entry[0] !== order.id);
          this._orders = observable.map([[order.id, order], ...entries]);
        } else {
          this._orders.set(order.id, order);
        }
      } else {
        this._orders = observable.map([[order.id, order], ...this._orders.entries()]);
      }
    } else if (!orderMeetsFilters && this._orders.has(order.id)) {
      this._orders.delete(order.id);
    }
  };

  public deleteOrder = (orderId: string) => {
    if (this._orders.has(orderId)) {
      this._orders.delete(orderId);
    }
  };

  // eslint-disable-next-line unused-imports/no-unused-vars
  public handleUpdateOrder(data: any) {
    return;
  }

  public get orders() {
    return [...this._orders.entries()].map(([_, item]) => item);
  }

  public clear = () => {
    this.ordersLoadingStatus = LoadingStatus.Init;
    this._orders = observable.map<string, TOrder>();
    this.pages = [];
  };
}
